// components/common/Changelog.tsx
/**
 * CHANGELOG.md, drawn: the build badge's popover shows the Unreleased section, the /changelog
 * page the whole file. Loading it is `hooks/useChangelog`.
 */
import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { type ChangelogRelease, parseInline } from '@/utils/changelog';

export const ChangelogText = ({ text }: { text: string }) => (
    <>
        {parseInline(text).map((part, i) => {
            switch (part.kind) {
                case 'strong':
                    return <strong key={i}>{part.text}</strong>;
                case 'code':
                    return <code key={i}>{part.text}</code>;
                case 'link':
                    return (
                        <Link key={i} href={part.href} target="_blank" rel="noopener noreferrer">
                            {part.text}
                        </Link>
                    );
                default:
                    return <React.Fragment key={i}>{part.text}</React.Fragment>;
            }
        })}
    </>
);

interface ReleaseProps {
    release: ChangelogRelease;
    /** The popover's size: smaller type, no release heading */
    compact?: boolean;
}

export const ChangelogReleaseView = ({ release, compact = false }: ReleaseProps) => {
    const text = compact ? 'caption' : 'body2';
    return (
        <Box>
            {!compact && (
                <Typography variant="h6" component="h2" sx={{ color: 'primary.main', mt: 3, mb: 1 }}>
                    {release.title}
                </Typography>
            )}
            {release.notes.map((note, i) => (
                <Typography key={i} variant={text} component="p" sx={{ color: 'text.secondary', mb: 1 }}>
                    <ChangelogText text={note} />
                </Typography>
            ))}
            {release.groups.map((group) => (
                <Box key={group.heading} sx={{ mb: compact ? 1 : 1.5 }}>
                    <Typography
                        variant="overline"
                        component="h3"
                        sx={{ display: 'block', color: 'text.secondary', lineHeight: 2 }}
                    >
                        {group.heading}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {group.items.map((item, i) => (
                            <Typography key={i} component="li" variant={text} sx={{ mb: 0.5 }}>
                                <ChangelogText text={item} />
                            </Typography>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};
